/**
 * Clients for the guided set-up screens (ADR-0064): identity pickers, the guardrails sample-text
 * tester, knowledge refresh, prompt "Try it" through the gateway and document text extraction.
 */
import type { Http } from "./http";
import type { Classification, DocumentAnalysis, RailAction } from "./types";

const enc = encodeURIComponent;

export interface PickRole {
  name: string;
  area: "work" | "build" | "govern" | "observe" | "channels" | "platform" | "organisation";
  /** The organisation's own roles (area organisation) carry their description (ADR-0102). */
  description?: string | null;
}
export interface PickGroup {
  name: string;
}

export function pickersApi(http: Http) {
  return {
    roles: () => http.get<PickRole[]>("identity", "/v1/pickers/roles"),
    groups: () => http.get<PickGroup[]>("identity", "/v1/pickers/groups"),
  };
}

/** `RailsTestIn` / `RailsTestOut` (POST /v1/configs/{tenant}/test, guardrails-admin). */
export interface RailsTestIn {
  agent?: string | null;
  direction: "input" | "output";
  text: string;
  draft?: Record<string, unknown> | null;
}
export interface RailsTestCategory {
  category: string;
  score: number;
  threshold: number;
  triggered: boolean;
  action: RailAction;
  kinds: Record<string, number>;
  /** Category "custom" (ADR-0111): which own guardrail, and its name in both languages. */
  rule?: string | null;
  name?: { en: string; ar: string } | null;
}
export interface RailsHighlight {
  start: number;
  end: number;
  category: string;
  /** What was found; for category "custom" the guardrail's id. */
  kind: string;
}
export interface RailsTestOut {
  decision: "allow" | "redact" | "block";
  categories: RailsTestCategory[];
  redacted_text?: string | null;
  highlights: RailsHighlight[];
  config_version: string;
}

export function railsTester(http: Http) {
  return (tenant: string, body: RailsTestIn) => http.post<RailsTestOut>("guardrails", `/v1/configs/${enc(tenant)}/test`, body);
}

/** `ReindexReport` of POST /v1/collections/{name}/reindex. */
export interface ReindexReport {
  collection: string;
  checked: number;
  changed: string[];
  unchanged: number;
  missing: string[];
  failed: Record<string, string>;
  stale: string[];
}

export function reindexer(http: Http) {
  return (collection: string) => http.post<ReindexReport>("retrieval", `/v1/collections/${enc(collection)}/reindex`);
}

/** One OpenAI-style chat completion through the gateway (metered under the caller). */
export interface TryPromptOut {
  text: string;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

/**
 * "Try it": sends the rendered prompt to `model` through the gateway, tagged as a prompt test
 * (`x_ap.task = "prompt-test"`) and never served from the response cache.
 */
export function promptTrier(http: Http) {
  return async (model: string, prompt: string, classification: Classification = "internal"): Promise<TryPromptOut> => {
    const res = await http.post<{
      model?: string;
      choices?: { message?: { content?: string | null } }[];
      usage?: { prompt_tokens?: number; completion_tokens?: number };
    }>("gateway", "/v1/chat/completions", {
      model,
      messages: [{ role: "user", content: prompt }],
      max_tokens: 512,
      x_ap: { task: "prompt-test", project: "prompt-registry", cache: false, data_classification: classification },
    });
    return {
      text: res.choices?.[0]?.message?.content ?? "",
      model: res.model ?? null,
      input_tokens: res.usage?.prompt_tokens ?? null,
      output_tokens: res.usage?.completion_tokens ?? null,
    };
  };
}

/** Full analysis with page text (GET /v1/documents/{id}). */
export interface DocumentWithText extends Omit<DocumentAnalysis, "pages"> {
  pages: { number: number; text: string }[];
}

export type ExtractFailure = "too_large" | "unsupported" | "encrypted" | "unsafe" | "unreadable" | "failed";

export class ExtractError extends Error {
  constructor(readonly reason: ExtractFailure) {
    super(reason);
    this.name = "ExtractError";
  }
}

/** Document types the knowledge upload accepts (text read in the browser; the rest via docintel). */
export const KNOWLEDGE_TEXT_TYPES = [".txt", ".md"];
export const KNOWLEDGE_DOC_TYPES = [".pdf", ".docx"];
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/**
 * Plain text of a PDF or Word file through the document-intelligence service: analyse (large
 * files become a job, polled), then read the pages. Errors carry a plain reason code.
 */
export function textExtractor(http: Http) {
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  return async (file: File, classification: Classification, opts: { pollMs?: number; maxPolls?: number } = {}): Promise<{ text: string; pages: number; language: string | null }> => {
    if (file.size > MAX_DOCUMENT_BYTES) throw new ExtractError("too_large");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("data_classification", classification);
    fd.append("mode", "auto");
    fd.append("source", "knowledge-upload");
    let res: { document_id?: string; job_id?: string; status?: string };
    try {
      res = await http.post<{ document_id?: string; job_id?: string; status?: string }>("docintel", "/v1/documents/analyze", fd);
    } catch (e) {
      const status = (e as { status?: number }).status;
      const msg = e instanceof Error ? e.message : "";
      if (/encrypt/i.test(msg)) throw new ExtractError("encrypted");
      if (/macro|active content/i.test(msg)) throw new ExtractError("unsafe");
      if (status === 413) throw new ExtractError("too_large");
      if (status === 415) throw new ExtractError("unsupported");
      if (status === 422 || status === 400) throw new ExtractError("unreadable");
      throw e;
    }
    let docId = res.document_id ?? null;
    if (!docId && res.job_id) {
      for (let i = 0; i < (opts.maxPolls ?? 120) && !docId; i++) {
        await sleep(opts.pollMs ?? 1000);
        const job = await http.get<{ status: string; document_id?: string | null }>("docintel", `/v1/jobs/${enc(res.job_id)}`);
        if (job.status === "failed") throw new ExtractError("failed");
        if (job.status === "succeeded" || job.document_id) docId = job.document_id ?? null;
      }
      if (!docId) throw new ExtractError("failed");
    }
    if (!docId) throw new ExtractError("failed");
    const doc = await http.get<DocumentWithText>("docintel", `/v1/documents/${enc(docId)}`);
    const text = doc.pages.map((p) => p.text).join("\n\n").trim();
    if (!text) throw new ExtractError("unreadable");
    return { text, pages: doc.pages.length, language: doc.language?.primary ?? null };
  };
}
